import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

export type Lang = 'en' | 'es';

interface Social {
  label: string;
  icon: string;
}

interface Member {
  id: number;
  name: string;
  role: string;
  location: string;        // display string, e.g. "Atlanta, GA"
  city: string;
  state: string;
  country: string;
  postalCode: string;
  category: string;
  image: string;
  price: number;               // static price for now — will come from backend package/subscription later
  businessType: 'individual' | 'business';
  verified: boolean;            // computed from price
  availableForHire: boolean;    // computed from price
  offersDiscounts: boolean;     // computed from price
  featured: boolean;            // computed from price
  acceptingClients: boolean;    // not price-based yet, manually set
  bookmarked: boolean;
  socials: Social[];
}

// ============ TIER THRESHOLDS ============
// TODO: keep this in sync with the backend config once packages/subscriptions are live.
// Once real data exists, these same thresholds should be applied server-side
// against each member's active package price, and the API should just return
// the computed booleans (verified, availableForHire, offersDiscounts, featured)
// with the same field names used here — so nothing in the template has to change.
const TIERS = {
  verifiedMin: 19.99,
  availableForHireMin: 2.54,
  offersDiscountMin: 49.99,
  featuredMinIndividual: 19.99,
  featuredMinBusiness: 109.99,
};

function computeTierFlags(price: number, businessType: 'individual' | 'business') {
  const featuredMin = businessType === 'business'
    ? TIERS.featuredMinBusiness
    : TIERS.featuredMinIndividual;

  return {
    verified: price >= TIERS.verifiedMin,
    availableForHire: price >= TIERS.availableForHireMin,
    offersDiscounts: price >= TIERS.offersDiscountMin,
    featured: price >= featuredMin,
  };
}

@Component({
  selector: 'app-directory',
  templateUrl: './directory.component.html',
  styleUrls: ['./directory.component.css']
})
export class DirectoryComponent implements OnInit {
  isNavOpen = false;
  
lang: Lang = 'en';

setLang(l: Lang) {
  this.lang = l;
}

get t() {
  return this.translations[this.lang];
}

// Only nav + directory strings are needed on this page.
translations = {
  en: {
    nav: {
      login: 'LOGIN',
      register: 'REGISTER NOW',
    },
    directory: {
      heroKicker: 'DISCOVER THE COMMUNITY',
      heroTitle1: 'DISCOVER. CONNECT.',
      heroTitle2: 'COLLABORATE.',
      heroSubtitle: 'Search the UMYO Español Directory and connect with creators, influencers, entrepreneurs, professionals, businesses, organizations, and community leaders.',
      searchSectionTitle: 'Search the Directory',
      searchPlaceholder: 'Search by name, business, skill, or keyword...',
      search: 'SEARCH',
      sortBy: 'Sort By',
      sortFeatured: 'Featured',
      sortNewest: 'Newest',
      sortAZ: 'A - Z',
      sortZA: 'Z - A',
      gridView: 'Grid View',
      listView: 'List View',
      showingPrefix: 'Showing',
      showingOf: 'of',
      members: 'Members',
      refineSearch: 'Refine Your Search',
      businessType: 'Business Type',
      allTypes: 'All Business Types',
      category: 'Category',
      allCategories: 'All Categories',
      country: 'Country',
      allCountries: 'All Countries',
      city: 'City',
      allCities: 'All Cities',
      state: 'State / Province',
      allStates: 'All States / Provinces',
      postalCode: 'Postal Code (Optional)',
      race: 'Race',
      allRaces: 'All Races',
      gender: 'Gender',
      allGenders: 'All Genders',
      priceRange: 'Price Range',
      features: 'Features',
      acceptingClients: 'Accepting Clients',
      availableForHire: 'Available for Hire',
      offersDiscounts: 'Offers Discounts',
      verifiedMembers: 'Verified Members',
      applyFilters: 'Apply Filters',
      resetFilters: 'Reset Filters',
      verified: 'VERIFIED',
      viewProfile: 'VIEW PROFILE',
      findTitle: 'Find the People You Need.',
      findSubtitle: 'Connect with creators, professionals, businesses, entrepreneurs, and organizations across the UMYO Español community.',
      exploreProfiles: 'Explore Profiles',
      ctaTitle1: 'TOGETHER,',
      ctaTitle2: 'WE GROW STRONGER.',
      ctaSubtitle: 'Join the UMYO Español Directory and become part of a powerful digital community built to connect our people, support our businesses, create opportunities, and grow together.',
    },
    footer: {
        tagline: 'PARA NOSOTROS. POR NOSOTROS. POTENCIADO POR UMYO.',
        quickLinks: 'ENLACES RÁPIDOS',
        resources: 'RECURSOS',
        connect: 'CONECTEMOS',
        copyright: '© 2026 Directorio UMYO Español. Todos los derechos reservados.'
      },
  },
  es: {
    nav: {
      login: 'INICIAR SESIÓN',
      register: 'REGISTRARSE AHORA',
    },
    directory: {
      heroKicker: 'DESCUBRE LA COMUNIDAD',
      heroTitle1: 'DESCUBRE. CONECTA.',
      heroTitle2: 'COLABORA.',
      heroSubtitle: 'Busca en el Directorio de UMYO Español y conéctate con creadores, influencers, emprendedores, profesionales, negocios, organizaciones y líderes de la comunidad.',
      searchSectionTitle: 'Busca en el Directorio',
      searchPlaceholder: 'Buscar por nombre, negocio, habilidad o palabra clave...',
      search: 'BUSCAR',
      sortBy: 'Ordenar por',
      sortFeatured: 'Destacado',
      sortNewest: 'Más reciente',
      sortAZ: 'A - Z',
      sortZA: 'Z - A',
      gridView: 'Vista de Cuadrícula',
      listView: 'Vista de Lista',
      showingPrefix: 'Mostrando',
      showingOf: 'de',
      members: 'miembros',
      refineSearch: 'Refina tu Búsqueda',
      businessType: 'Tipo de Negocio',
      allTypes: 'Todos los Tipos de Negocio',
      category: 'Categoría',
      allCategories: 'Todas las Categorías',
      country: 'País',
      allCountries: 'Todos los Países',
      city: 'Ciudad',
      allCities: 'Todas las Ciudades',
      state: 'Estado / Provincia',
      allStates: 'Todos los Estados / Provincias',
      postalCode: 'Código Postal (Opcional)',
      race: 'Raza',
      allRaces: 'Todas las Razas',
      gender: 'Género',
      allGenders: 'Todos los Géneros',
      priceRange: 'Rango de Precios',
      features: 'Características',
      acceptingClients: 'Aceptando Clientes',
      availableForHire: 'Disponible para Contratar',
      offersDiscounts: 'Ofrece Descuentos',
      verifiedMembers: 'Miembros Verificados',
      applyFilters: 'Aplicar Filtros',
      resetFilters: 'Restablecer Filtros',
      verified: 'VERIFICADO',
      viewProfile: 'VER PERFIL',
      findTitle: 'Encuentra a las Personas que Necesitas.',
      findSubtitle: 'Conéctate con creadores, profesionales, negocios, emprendedores y organizaciones en toda la comunidad de UMYO Español.',
      exploreProfiles: 'Explorar Perfiles',
      ctaTitle1: 'JUNTOS,',
      ctaTitle2: 'CRECEMOS MÁS FUERTES.',
      ctaSubtitle: 'Únete al Directorio de UMYO Español y sé parte de una poderosa comunidad digital creada para conectar a nuestra gente, apoyar nuestros negocios, crear oportunidades y crecer juntos.',
    },
    footer: {
        tagline: 'PARA NOSOTROS. POR NOSOTROS. POTENCIADO POR UMYO.',
        quickLinks: 'ENLACES RÁPIDOS',
        resources: 'RECURSOS',
        connect: 'CONECTEMOS',
        copyright: '© 2026 Directorio UMYO Español. Todos los derechos reservados.'
    },
  },
};
  // ============ TOP BAR ============
  searchTerm = new FormControl('');
  sortBy = new FormControl('featured');
  viewMode: 'grid' | 'list' = 'grid';

  // ============ FILTER DROPDOWN OPTIONS ============
  // TODO: in ko API se load karo (jaise home component me businessType/location waghera already load ho rahay hain)
  businessType: string[] = ['Content Creator', 'Influencer', 'Music', 'Beauty & Wellness', 'Health & Fitness', 'Business Services', 'Food & Beverage', 'Events', 'Photography'];
  category: string[] = ['Media & Entertainment', 'Beauty & Wellness', 'Health & Fitness', 'Business Services', 'Food & Beverage', 'Events', 'Photography'];
  // TODO: replace with real country list from API once available
  country: string[] = ['United States', 'Mexico', 'Puerto Rico', 'Spain', 'Colombia', 'Dominican Republic', 'Argentina', 'Other'];
  city: string[] = ['Atlanta', 'Miami', 'Los Angeles', 'Dallas', 'Houston', 'Charlotte', 'New York'];
  state: string[] = ['Georgia', 'Florida', 'California', 'Texas', 'North Carolina', 'New York'];
  race: string[] = ['Black', 'White', 'Hispanic', 'Asian', 'Mixed', 'Other'];
  gender: string[] = ['Male', 'Female', 'Non-Binary'];

  // ============ FILTER FORM ============
  filterForm: FormGroup;

  // ============ MEMBERS / PAGINATION ============
  allMembers: Member[] = [];   // full unfiltered list (mock / from API)
  members: Member[] = [];      // currently displayed page

  currentPage = 1;
  pageSize = 12;
  totalMembers = 0;
  totalPages = 0;
  visiblePages: number[] = [];

  constructor(private fb: FormBuilder) {
    this.filterForm = this.fb.group({
      businessType: [''],
      category: [''],
      country: [''],
      city: [''],
      state: [''],
      postalCode: [''],
      race: [''],
      gender: [''],
      priceRange: [500],
      acceptingClients: [false],
      availableForHire: [false],
      offersDiscounts: [false],
      verifiedOnly: [false]
    });
  }

  ngOnInit(): void {
    this.loadMembers();
  }

  // ============ DATA LOADING ============
  // TODO: is mock data ko apni API call se replace karo (jaise home component me members/cards fetch hotay hain)
  // Jab backend ready ho: har member ke API response mein price/businessType ki jagah
  // seedha verified/availableForHire/offersDiscounts/featured booleans aayenge —
  // us waqt computeTierFlags() call karna chhod dena, bas API se aaye flags use kar lena.
  loadMembers(): void {
    this.allMembers = this.getMockMembers();
    this.totalMembers = this.allMembers.length;
    this.setPage(1);
  }

  getMockMembers(): Member[] {
    const socials: Social[] = [
      { label: 'Instagram', icon: '◎' },
      { label: 'TikTok', icon: '♪' },
      { label: 'YouTube', icon: '▶' },
      { label: 'X', icon: '𝕏' }
    ];

    const names: Array<{
      name: string; role: string; city: string; state: string; postalCode: string; category: string;
      price: number; businessType: 'individual' | 'business'; acceptingClients: boolean;
    }> = [
      { name: 'Lady Luxe', role: 'Content Creator | Influencer', city: 'Atlanta', state: 'Georgia', postalCode: '30301', category: 'Media & Entertainment', price: 19.99, businessType: 'individual', acceptingClients: true },
      { name: 'Yung Reel', role: 'Music Producer', city: 'Miami', state: 'Florida', postalCode: '33101', category: 'Music', price: 2.54, businessType: 'individual', acceptingClients: false },
      { name: 'Glam Beauty', role: 'Beauty Studio', city: 'Los Angeles', state: 'California', postalCode: '90001', category: 'Beauty & Wellness', price: 109.99, businessType: 'business', acceptingClients: true },
      { name: 'Fit Focus', role: 'Fitness Trainer', city: 'Dallas', state: 'Texas', postalCode: '75201', category: 'Health & Fitness', price: 49.99, businessType: 'individual', acceptingClients: true },
      { name: 'Boss Moves Agency', role: 'Marketing Agency', city: 'Houston', state: 'Texas', postalCode: '77001', category: 'Business Services', price: 109.99, businessType: 'business', acceptingClients: false },
      { name: 'Chef Nyla', role: 'Private Chef', city: 'Charlotte', state: 'North Carolina', postalCode: '28201', category: 'Food & Beverage', price: 0, businessType: 'individual', acceptingClients: true },
      { name: 'Elite Events', role: 'Event Planner', city: 'Atlanta', state: 'Georgia', postalCode: '30302', category: 'Events', price: 49.99, businessType: 'business', acceptingClients: true },
      { name: 'Creative Vision Co.', role: 'Photography', city: 'New York', state: 'New York', postalCode: '10001', category: 'Photography', price: 19.99, businessType: 'individual', acceptingClients: false }
    ];

    // 12 x repeat karke pagination demo ke liye ~40 members bana rahay hain
    const members: Member[] = [];
    let id = 1;
    for (let i = 0; i < 5; i++) {
      for (const n of names) {
        const flags = computeTierFlags(n.price, n.businessType);
        members.push({
          id: id,
          name: n.name,
          role: n.role,
          location: `${n.city}, ${n.state}`,
          city: n.city,
          state: n.state,
          country: 'United States',
          postalCode: n.postalCode,
          category: n.category,
          image: `assets/images/members/member${((id - 1) % 8) + 1}.png`,
          price: n.price,
          businessType: n.businessType,
          acceptingClients: n.acceptingClients,
          bookmarked: false,
          socials,
          ...flags
        });
        id++;
      }
    }
    return members;
  }

  // ============ SEARCH / FILTER ============
  searchMembers(): void {
    this.setPage(1);
  }

  applyFilters(): void {
    this.setPage(1);
  }

  resetFilters(): void {
    this.filterForm.reset({
      businessType: '',
      category: '',
      country: '',
      city: '',
      state: '',
      postalCode: '',
      race: '',
      gender: '',
      priceRange: 500,
      acceptingClients: false,
      availableForHire: false,
      offersDiscounts: false,
      verifiedOnly: false
    });
    this.searchTerm.setValue('');
    this.setPage(1);
  }

  private getFilteredMembers(): Member[] {
    const term = (this.searchTerm.value || '').toLowerCase().trim();
    const f = this.filterForm.value;

    return this.allMembers.filter(m => {
      if (term && !(m.name.toLowerCase().includes(term) || m.role.toLowerCase().includes(term) || m.category.toLowerCase().includes(term))) {
        return false;
      }
      if (f.businessType && m.role !== f.businessType) return false;
      if (f.category && m.category !== f.category) return false;
      if (f.country && m.country !== f.country) return false;
      if (f.state && m.state !== f.state) return false;
      if (f.city && m.city !== f.city) return false;
      if (f.postalCode && !m.postalCode.toLowerCase().includes(f.postalCode.toLowerCase().trim())) return false;
      if (f.acceptingClients && !m.acceptingClients) return false;
      if (f.availableForHire && !m.availableForHire) return false;
      if (f.offersDiscounts && !m.offersDiscounts) return false;
      if (f.verifiedOnly && !m.verified) return false;
      if (typeof f.priceRange === 'number' && m.price > f.priceRange) return false;
      return true;
    });
  }

  // ============ BOOKMARK ============
  toggleBookmark(member: Member): void {
    member.bookmarked = !member.bookmarked;
    // TODO: yahan API call karo save/remove bookmark ke liye
  }

  // ============ PAGINATION ============
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.setPage(page);
  }

  private setPage(page: number): void {
    const filtered = this.getFilteredMembers();
    this.totalMembers = filtered.length;
    this.totalPages = Math.max(1, Math.ceil(this.totalMembers / this.pageSize));
    this.currentPage = Math.min(page, this.totalPages);

    const start = (this.currentPage - 1) * this.pageSize;
    this.members = filtered.slice(start, start + this.pageSize);

    this.updateVisiblePages();
  }

  private updateVisiblePages(): void {
    const maxButtons = 3;
    const pages: number[] = [];
    const start = Math.max(1, this.currentPage - 1);
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    this.visiblePages = pages;
  }
}